import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import type { PublicDeclaredLoadTracking } from '../../types/publicTracking';

const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

function cancelTokenFor(signal?: AbortSignal) {
  if (!signal) return undefined;

  const source = axios.CancelToken.source();

  if (signal.aborted) {
    source.cancel('aborted');
  } else {
    signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
  }

  return source.token;
}

export class PublicTrackingUnavailableError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'PublicTrackingUnavailableError';
    this.status = status;
  }
}

function pick<T>(source: Record<string, unknown>, camel: string, snake: string): T | undefined {
  return (source[camel] ?? source[snake]) as T | undefined;
}

function normalizeTrackingPoint(value: unknown) {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    latitude: pick<number | null>(source, 'latitude', 'latitude'),
    longitude: pick<number | null>(source, 'longitude', 'longitude'),
    accuracyM: pick<number | null>(source, 'accuracyM', 'accuracy_m'),
    headingDeg: pick<number | null>(source, 'headingDeg', 'heading_deg'),
    speedMps: pick<number | null>(source, 'speedMps', 'speed_mps'),
    at: pick<string | null>(source, 'at', 'at'),
  };
}

export function normalizePublicDeclaredLoadTracking(input: unknown): PublicDeclaredLoadTracking {
  const source = (input ?? {}) as Record<string, unknown>;
  const driver = (pick<Record<string, unknown> | null>(source, 'driver', 'driver') ?? {}) as Record<
    string,
    unknown
  >;
  const vehicle = (pick<Record<string, unknown> | null>(source, 'vehicle', 'vehicle') ?? {}) as Record<
    string,
    unknown
  >;
  const tracking = (pick<Record<string, unknown> | null>(source, 'tracking', 'tracking') ?? {}) as Record<
    string,
    unknown
  >;
  const timeline = pick<unknown[] | null>(source, 'timeline', 'timeline');
  const breadcrumb = pick<unknown[] | null>(tracking, 'breadcrumb', 'breadcrumb');

  return {
    bookingReference: pick<string>(source, 'bookingReference', 'booking_reference') ?? '',
    status: pick<string | null>(source, 'status', 'status'),
    scheduledAt: pick<string | null>(source, 'scheduledAt', 'scheduled_at'),
    pickup: pick(source, 'pickup', 'pickup'),
    destination: pick(source, 'destination', 'destination'),
    driver: {
      name: pick<string | null>(driver, 'name', 'name'),
      rating: pick<number | null>(driver, 'rating', 'rating'),
      avatarUrl: pick<string | null>(driver, 'avatarUrl', 'avatar_url'),
    },
    vehicle: {
      type: pick<string | null>(vehicle, 'type', 'type'),
      displayName: pick<string | null>(vehicle, 'displayName', 'display_name'),
    },
    tracking: {
      phase: pick<string | null>(tracking, 'phase', 'phase'),
      driverLatitude: pick<number | null>(tracking, 'driverLatitude', 'driver_latitude'),
      driverLongitude: pick<number | null>(tracking, 'driverLongitude', 'driver_longitude'),
      accuracyM: pick<number | null>(tracking, 'accuracyM', 'accuracy_m'),
      headingDeg: pick<number | null>(tracking, 'headingDeg', 'heading_deg'),
      speedMps: pick<number | null>(tracking, 'speedMps', 'speed_mps'),
      locationAt: pick<string | null>(tracking, 'locationAt', 'location_at'),
      etaToPickupSec: pick<number | null>(tracking, 'etaToPickupSec', 'eta_to_pickup_sec'),
      etaToDropoffSec: pick<number | null>(tracking, 'etaToDropoffSec', 'eta_to_dropoff_sec'),
      locationKind: pick<string | null>(tracking, 'locationKind', 'location_kind'),
      displayLabel: pick<string | null>(tracking, 'displayLabel', 'display_label'),
      nearestTowns: pick<unknown[] | null>(tracking, 'nearestTowns', 'nearest_towns'),
      breadcrumb: Array.isArray(breadcrumb) ? breadcrumb.map(normalizeTrackingPoint) : null,
      routePolyline: pick<string | null>(tracking, 'routePolyline', 'route_polyline'),
      distanceKm: pick<number | null>(tracking, 'distanceKm', 'distance_km'),
      durationSec: pick<number | null>(tracking, 'durationSec', 'duration_sec'),
      routeRevision: pick<number | null>(tracking, 'routeRevision', 'route_revision'),
      routeDeviation: pick<boolean | null>(tracking, 'routeDeviation', 'route_deviation'),
      routeUpdatedAt: pick<string | null>(tracking, 'routeUpdatedAt', 'route_updated_at'),
      routeAvailable: pick<boolean | null>(tracking, 'routeAvailable', 'route_available'),
      routeStatus: pick<string | null>(tracking, 'routeStatus', 'route_status'),
      routeErrorCode: pick<string | null>(tracking, 'routeErrorCode', 'route_error_code'),
      locationTrusted: pick<boolean | null>(tracking, 'locationTrusted', 'location_trusted'),
      locationRejectedReason: pick<string | null>(
        tracking,
        'locationRejectedReason',
        'location_rejected_reason',
      ),
      trackingMode: pick<string | null>(tracking, 'trackingMode', 'tracking_mode'),
      expectedUpdateIntervalSec: pick<number | null>(
        tracking,
        'expectedUpdateIntervalSec',
        'expected_update_interval_sec',
      ),
      driverFinishingAnotherDelivery: pick<boolean | null>(
        tracking,
        'driverFinishingAnotherDelivery',
        'driver_finishing_another_delivery',
      ),
      driverStartsToPickupEtaSec: pick<number | null>(
        tracking,
        'driverStartsToPickupEtaSec',
        'driver_starts_to_pickup_eta_sec',
      ),
      trackingMessage: pick<string | null>(tracking, 'trackingMessage', 'tracking_message'),
      lastSeenAt: pick<string | null>(tracking, 'lastSeenAt', 'last_seen_at'),
      updatedAt: pick<string | null>(tracking, 'updatedAt', 'updated_at'),
    },
    timeline: Array.isArray(timeline)
      ? timeline.map((item) => {
          const row = (item ?? {}) as Record<string, unknown>;
          return {
            key: String(pick(row, 'key', 'key') ?? ''),
            label: String(pick(row, 'label', 'label') ?? ''),
            at: pick<string | null>(row, 'at', 'at'),
            completed: Boolean(pick<boolean | null>(row, 'completed', 'completed')),
          };
        })
      : null,
    chatEnabled: Boolean(pick<boolean | null>(source, 'chatEnabled', 'chat_enabled')),
    publicChannelId: pick<string | null>(source, 'publicChannelId', 'public_channel_id'),
  };
}

export async function getPublicDeclaredLoadTracking(
  token: string,
  options?: { signal?: AbortSignal },
): Promise<PublicDeclaredLoadTracking> {
  try {
    const response = await publicApi.get(
      `booking-management/api/public/declared-load-tracking/${encodeURIComponent(token)}`,
      {
        cancelToken: cancelTokenFor(options?.signal),
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 404 || status === 410,
      },
    );

    if (response.status === 404) {
      throw new PublicTrackingUnavailableError('This tracking link was not found.', 404);
    }

    if (response.status === 410) {
      throw new PublicTrackingUnavailableError('This tracking link has expired.', 410);
    }

    return normalizePublicDeclaredLoadTracking(response.data);
  } catch (error) {
    if (axios.isCancel(error)) throw error;

    if (error instanceof PublicTrackingUnavailableError) {
      throw error;
    }

    if (axios.isAxiosError(error)) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        throw new PublicTrackingUnavailableError(
          'We could not reach the ShiftWagon tracking service right now.',
        );
      }

      const status = error.response?.status;
      throw new PublicTrackingUnavailableError('Tracking is temporarily unavailable.', status);
    }

    throw new PublicTrackingUnavailableError('Tracking is temporarily unavailable.');
  }
}
